import { useContext, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { Text, tokens } from '@hotjar/hotsauce';
import { SidebarCategoryButton } from '@hotjar/insights-common/components/SidebarCategory/SidebarCategoryButton';
import type { CSSProperties } from 'react';
import type { ListChildComponentProps } from 'react-window';
import { useTranslation } from '@hotjar/translations';
import { SideBarItem } from '../SideBarItem';
import { SURVEY_NAME_MAX_LENGTH } from '../../../../domain/Surveys/Survey/Survey';
import { FEEDBACK_NAME_MAX_LENGTH } from '../../../../domain/IncomingFeedback/Feedback';
import { DynamicListContext, ROW_GUTTER_SIZE } from './WidgetListSideBar';

import type { RowData } from './WidgetListSideBar';

const NAME_MAX_LENGTH = {
  survey: SURVEY_NAME_MAX_LENGTH,
  feedback: FEEDBACK_NAME_MAX_LENGTH,
};

const RowContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  padding: 0 3px;
`;

const EmptyText = styled(Text)`
  margin: 0 ${tokens.spacer.x2} ${tokens.spacer.x1} ${tokens.spacer.x2};
`;

function addGutter(style: CSSProperties, index: number): CSSProperties {
  return { ...style, top: (style.top as number) + (index !== 0 ? ROW_GUTTER_SIZE : 0) };
}

export const Row = ({ index, data, style }: ListChildComponentProps<RowData[]>): JSX.Element => {
  const items = data;
  const rowData = items[index];
  const { setSize } = useContext(DynamicListContext);
  const rowRoot = useRef<null | HTMLDivElement>(null);
  const { t } = useTranslation('feedback');

  useEffect(() => {
    if (rowRoot.current) {
      setSize && setSize(index, rowRoot.current.getBoundingClientRect().height);
    }
  }, [index, setSize, rowData.type]);

  return (
    <div style={addGutter(style, index)}>
      <RowContainer ref={rowRoot}>
        {rowData.type === 'empty' && (
          <EmptyText textType="body2" textColor="text_disabled">
            {rowData.text}
          </EmptyText>
        )}
        {rowData.type === 'widget' && (
          <SideBarItem
            title={rowData.widget.name}
            count={rowData.widget.response_count}
            id={rowData.widget.id}
            href={rowData.href}
            nameMaxLength={NAME_MAX_LENGTH[rowData.widgetType]}
            onEdit={
              rowData.hasUpdatePermission
                ? () => rowData.onActionSelected('edit', rowData.widget)
                : undefined
            }
            onDuplicate={
              rowData.hasDuplicatePermission
                ? () => rowData.onActionSelected('duplicate', rowData.widget)
                : undefined
            }
            onCopyLink={() => rowData.onActionSelected('copy_link', rowData.widget)}
            onDelete={
              rowData.hasDeletePermission
                ? () => rowData.onActionSelected('delete', rowData.widget)
                : undefined
            }
            onRename={
              rowData.hasUpdatePermission
                ? () => rowData.onActionSelected('rename', rowData.widget)
                : undefined
            }
          />
        )}
        {rowData.type === 'all' && (
          <SideBarItem
            id={0}
            hideMenu
            title={t('survey-list.header.all-surveys')}
            count={rowData.count}
            href={rowData.href}
            nameMaxLength={SURVEY_NAME_MAX_LENGTH}
          />
        )}
        {rowData.type === 'category' && (
          <SidebarCategoryButton
            title={rowData.title}
            onClick={rowData.onClick}
            isExpanded={rowData.isExpanded}
          />
        )}
      </RowContainer>
    </div>
  );
};
